/**
 * Slide registry — single source of truth for which SlideVariant is
 * picked for each `slide.type` string. Adding a new slide type means
 * (1) adding a class entry here, (2) implementing a variant file in
 * slides/variants.
 *
 * Keeping the registry tiny means the dispatch is testable in
 * isolation and the per-type files stay focused on visual concerns.
 */
import { TitleVariant } from './variants/TitleVariant';
import { MessageVariant } from './variants/MessageVariant';
import { PhotoVariant } from './variants/PhotoVariant';
import { MemoryVariant } from './variants/MemoryVariant';
import { QuestionVariant } from './variants/QuestionVariant';
import { FinalVariant } from './variants/FinalVariant';
import type { ComponentType } from 'react';
import type { PreviewSlideData } from '../PreviewSlide';

export interface VariantProps {
  slide: PreviewSlideData;
}

type VariantComponent = ComponentType<VariantProps>;

/** Map from PreviewSlideData.type → renderer. Unknown types fall back to
 *  MessageVariant, matching legacy behaviour. */
export const SLIDE_VARIANTS: Record<string, VariantComponent> = {
  TITLE: TitleVariant,
  MESSAGE: MessageVariant,
  PHOTO: PhotoVariant,
  MEMORY: MemoryVariant,
  QUESTION: QuestionVariant,
  FINAL: FinalVariant,
};

export function resolveVariant(type: string | undefined): VariantComponent {
  if (!type) return MessageVariant;
  return SLIDE_VARIANTS[type] ?? MessageVariant;
}

/** Default entry-effect code for each slide type. Admins can override
 *  per-slide via `effectEntry`.  */
export const DEFAULT_ENTRY_BY_TYPE: Record<string, string> = {
  TITLE: 'fade',
  MESSAGE: 'fade',
  PHOTO: 'ken-burns',
  MEMORY: 'fade',
  QUESTION: 'letter-rise',
  FINAL: 'fade',
};
